// What the city screens read off a village/get answer: queues with their real timings, movements grouped the way the Rally
// Point shows them, production and storage math, the Farm's population breakdown, research levels. Pure functions over the
// Village shape (docs/api-phase1.md) — no fetching, no React.
import type {
  AuroraQuests, BuildingDef, ConstructionItem, DetailTable, MarketMovement, Movement, QuestCategory, QuestItem, RecruitItem, ResearchItem, StoreItem, TechDef, TroopDef, Village,
} from "@/api/types";
import {
  BUILDING_KEYS, BUILDING_NAMES, RESOURCE_KEYS, TROOP_BUILDING, TROOP_KEYS, TROOP_NAMES, type BuildingKey, type ResourceKey, type TroopKey,
} from "./constants";
import { fmtDuration, fmtSpan, parseGameTime, parseHMS, parseMinSec } from "./time";
import { UNITS, WORLD, fieldsBetween, slowestSpeed, travelMs } from "./rules";
import { fmtNum, parseCoords } from "./format";

// ------------------------------------------------------------------ buildings

export const buildingMap = (v: Village): Record<BuildingKey, BuildingDef> => {
  const out = {} as Record<BuildingKey, BuildingDef>;
  for (const b of v.buildings ?? []) out[b.key] = b;
  return out;
};

export const levelOf = (v: Village | undefined, key: BuildingKey): number => v?.buildings?.find((b) => b.key === key)?.level ?? 0;

/** `next_level` is the string "-1" at max; otherwise the level the next queued upgrade would reach. */
export const isMaxed = (b: BuildingDef) => String(b.next_level) === "-1" || b.level >= b.max;

export const nextLevel = (b: BuildingDef): number | null => (isMaxed(b) ? null : Number(b.next_level));

/** "Required: CityHall (Level 3) Barracks (Level 1)" → ["City Hall 3", "Barracks 1"] */
export function prereqList(s: string | undefined): string[] {
  if (!s) return [];
  const out: string[] = [];
  const re = /([A-Za-z]+)\s*\(Level\s*(\d+)\)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s))) {
    const key = BUILDING_KEYS.find((k) => BUILDING_NAMES[k].replace(/\s/g, "").toLowerCase() === m![1].toLowerCase());
    out.push(`${key ? BUILDING_NAMES[key] : m[1]} ${m[2]}`);
  }
  return out;
}

export interface Cost {
  r_w: number;
  r_c: number;
  r_i: number;
  p_u?: number;
  /** ms */
  time?: number;
}

export const buildingCost = (b: BuildingDef): Cost => ({ r_w: b.r_w, r_c: b.r_c, r_i: b.r_i, p_u: b.p_u, time: parseHMS(b.time) });

/** Which resources fall short for a cost, and how long the city's production needs to cover the worst one. */
export function shortfall(v: Village, cost: Cost): { short: Partial<Record<ResourceKey, number>>; waitMs: number; pop: boolean } {
  const short: Partial<Record<ResourceKey, number>> = {};
  let waitMs = 0;
  for (const r of RESOURCE_KEYS) {
    const need = cost[r] - (v.resources?.[r] ?? 0);
    if (need > 0) {
      short[r] = need;
      const rate = v.resources_production?.[r] ?? 0;
      waitMs = Math.max(waitMs, rate > 0 ? (need / rate) * 3600_000 : Infinity);
    }
  }
  const pop = (cost.p_u ?? 0) > freePopulation(v);
  return { short, waitMs, pop };
}

export const canAfford = (v: Village, cost: Cost) => {
  const s = shortfall(v, cost);
  return Object.keys(s.short).length === 0 && !s.pop;
};

/** Why an upgrade (or, with `demolish`, a downgrade) of `b` can't be queued right now — null when it can. Shared by City
 * Hall's rows and the city view's construction mode so both say the same thing. `slotOpensInMs` is the time left on the
 * job now building (the first slot to free up). */
export function buildBlock(v: Village, b: BuildingDef, o: { queueFull: boolean; slotOpensInMs?: number; demolish?: boolean }): string | null {
  if (o.queueFull) return o.slotOpensInMs !== undefined ? `Queue full — a slot opens in ${fmtDuration(Math.max(0, o.slotOpensInMs))}` : "Queue full";
  if (o.demolish) {
    if (!v.can_demolish) return "Downgrading needs City Hall 15";
    return Number(b.next_level_demolish) < 0 ? "This building can't be downgraded further" : null;
  }
  if (isMaxed(b)) return "This building is at its final level";
  if (!b.has_prereqs) {
    const p = prereqList(b.prereqs_string);
    return p.length ? `Requires ${p.join(", ")}` : "Requirements not met";
  }
  const cost = buildingCost(b);
  const s = shortfall(v, cost);
  if (s.pop) return `Needs ${fmtNum(cost.p_u ?? 0)} free population — upgrade the Farm`;
  const short = Object.entries(s.short) as [ResourceKey, number][];
  if (short.length) {
    const [r, n] = short[0];
    const name = r === "r_w" ? "wood" : r === "r_c" ? "clay" : "iron";
    return `${fmtNum(n)} ${name} short${Number.isFinite(s.waitMs) ? ` · about ${fmtSpan(s.waitMs)}` : ""}`;
  }
  return null;
}

// ------------------------------------------------------------------ resources, storage, population

export const capacity = (v: Village) => v.resources_capacity ?? 0;

/** ms until the warehouse is full for one resource at the current rate (Infinity when nothing is produced or it is full). */
export function fullInMs(v: Village, r: ResourceKey): number {
  const rate = v.resources_production?.[r] ?? 0;
  const room = capacity(v) - (v.resources?.[r] ?? 0);
  if (room <= 0) return 0;
  return rate > 0 ? (room / rate) * 3600_000 : Infinity;
}

export const freePopulation = (v: Village) => Math.max(0, (v.max_population ?? 0) - (v.current_population ?? 0));

/** The Farm's breakdown from building_details.b_f ("Current Population" table). */
export function populationBreakdown(v: Village): { buildings: number; buildingsQueued: number; troops: number; troopsQueued: number } | null {
  const t = tableNamed(v, "b_f", "Current Population");
  if (!t) return null;
  const row = (label: string) => Number(t.table.find((r) => label in r)?.[label] ?? 0);
  return { buildings: row("Buildings"), buildingsQueued: row("Buildings in production"), troops: row("Troops"), troopsQueued: row("Troops in production") };
}

export const tableNamed = (v: Village, key: BuildingKey, header: string): DetailTable | undefined =>
  v.building_details?.[key]?.tables?.find((t) => t.header === header || t.header.startsWith(header));

/** "Current Level (L)" / "Next Level (L+1)" rows of a details table as numbers (production rows are "134 hr" strings). */
export function nowNext(t: DetailTable | undefined): { now: number | null; next: number | null } {
  if (!t) return { now: null, next: null };
  const num = (x: unknown) => {
    if (x === undefined || x === null) return null;
    const n = typeof x === "number" ? x : Number(String(x).replace(/[^0-9.\-]/g, ""));
    return Number.isFinite(n) ? n : null;
  };
  const find = (prefix: string) => t.table.find((r) => Object.keys(r).some((k) => k.startsWith(prefix)));
  const nowRow = find("Current Level");
  const nextRow = find("Next Level");
  return {
    now: nowRow ? num(Object.values(nowRow)[0]) : null,
    next: nextRow ? num(Object.values(nextRow)[0]) : null,
  };
}

// ------------------------------------------------------------------ construction queue

export interface QueueEntry {
  item: ConstructionItem;
  index: number;
  startMs: number;
  /** the real end: end_time − speedup_timespan when a speed-up was used */
  endMs: number;
  durationMs: number;
}

export function constructionQueue(v: Village): QueueEntry[] {
  return (v.queue_hq ?? []).map((item, index) => {
    const startMs = parseGameTime(item.start_time);
    const endRaw = parseGameTime(item.end_time);
    const endMs = item.has_used_speedup ? endRaw - (item.speedup_timespan || 0) : endRaw;
    return { item, index, startMs, endMs, durationMs: Math.max(0, endMs - startMs) };
  });
}

export const queueProgress = (e: QueueEntry, now: number) => (e.durationMs > 0 ? Math.min(1, Math.max(0, (now - e.startMs) / e.durationMs)) : 1);

/** Levels already queued for a building (the game's `next_level` includes them). */
export const queuedLevels = (v: Village, key: BuildingKey) => (v.queue_hq ?? []).filter((q) => q.key === key).length;

// ------------------------------------------------------------------ recruit queue

export interface RecruitEntry {
  item: RecruitItem;
  building: BuildingKey;
  endMs: number;
  startMs: number;
  troop: TroopKey;
  name: string;
}

export function recruitQueue(v: Village, building?: BuildingKey): RecruitEntry[] {
  const out: RecruitEntry[] = [];
  const q = v.queue_troops ?? {};
  for (const [bk, items] of Object.entries(q) as [BuildingKey, RecruitItem[] | undefined][]) {
    if (building && bk !== building) continue;
    for (const item of items ?? []) {
      out.push({
        item,
        building: bk,
        startMs: item.start_time_ms ?? parseGameTime(item.start_time),
        endMs: item.end_time_ms ?? parseGameTime(item.end_time),
        troop: item.primary_type,
        name: TROOP_NAMES[item.primary_type] ?? item.primary_type,
      });
    }
  }
  return out.sort((a, b) => a.endMs - b.endMs);
}

/** Units finished so far, counting from the server's last update at the current pace. */
export function recruitDone(e: RecruitEntry, now: number): number {
  const per = e.item.unit_duration_ms || 0;
  const base = e.item.completed_units ?? 0;
  const since = e.item.last_updated_at_ms ? now - e.item.last_updated_at_ms : 0;
  const extra = per > 0 && since > 0 ? since / per : 0;
  return Math.min(e.item.num_units, Math.floor(base + extra));
}

// ------------------------------------------------------------------ troops & research

export const troopDefs = (v: Village, building: BuildingKey): TroopDef[] => (v.troops?.[building] ?? []) as TroopDef[];

export const troopCost = (t: TroopDef): Cost => ({ r_w: t.r_w, r_c: t.r_c, r_i: t.r_i, p_u: t.p_u, time: parseMinSec(t.time) });

export const homeCount = (v: Village, key: TroopKey) => v.troops_in_village?.[key] ?? 0;

/** Research level of a troop (0 = locked). Scholars have no research. */
export function researchLevel(v: Village, key: TroopKey): number {
  if (key === "t_n") return 1;
  const tech = v.research?.find((r) => r.image_key === key);
  return tech?.level ?? 0;
}

export const techFor = (v: Village, key: TroopKey): TechDef | undefined => v.research?.find((r) => r.image_key === key);

export const researchPoints = (v: Village) => ({
  max: Number(v.research_details?.max_points ?? WORLD.research.maxPoints),
  used: Number(v.research_details?.allocated_points ?? 0),
});

export interface ResearchEntry {
  item: ResearchItem;
  index: number;
  endMs: number;
  durationMs: number;
  troop: TroopKey | undefined;
}

export function researchQueue(v: Village): ResearchEntry[] {
  return (v.queue_research ?? []).map((item, index) => ({
    item,
    index,
    endMs: parseGameTime(item.end_time),
    durationMs: parseHMS(Array.isArray(item.duration) ? item.duration[0] : (item.duration as unknown as string)),
    troop: TROOP_KEYS.find((k) => TROOP_NAMES[k] === item.name),
  }));
}

/** Troops home plus their population (own troops only). */
export function garrison(v: Village): { troops: number; pop: number; byKey: Record<TroopKey, number> } {
  const byKey = {} as Record<TroopKey, number>;
  let troops = 0;
  let pop = 0;
  for (const k of TROOP_KEYS) {
    const n = v.troops_in_village?.[k] ?? 0;
    byKey[k] = n;
    troops += n;
    pop += n * (UNITS[k]?.pop ?? 0);
  }
  return { troops, pop, byKey };
}

export const sumTroops = (c: Partial<Record<TroopKey, number>> | undefined) => (c ? Object.values(c).reduce((a, b) => a + (b || 0), 0) : 0);

export const buildingOfTroop = (k: TroopKey): BuildingKey => TROOP_BUILDING[k];

// ------------------------------------------------------------------ movements

export type MoveKind = "attack" | "support" | "return-attack" | "return-support";

export interface MoveEntry {
  m: Movement;
  dir: "in" | "out";
  kind: MoveKind;
  startMs: number;
  endMs: number;
  cancelUntilMs: number;
  units: Partial<Record<TroopKey, number>> | undefined;
  /** the other city, as "name (x, y)" parts */
  other: { name: string; coords: string };
}

const KIND: Record<string, MoveKind> = { a: "attack", s: "support", ra: "return-attack", rp: "return-support", rs: "return-support" };

export function movements(v: Village): { incoming: MoveEntry[]; outgoing: MoveEntry[] } {
  const mk = (m: Movement, dir: "in" | "out"): MoveEntry => ({
    m,
    dir,
    kind: KIND[m.subtype] ?? "attack",
    startMs: parseGameTime(m.start_time),
    endMs: parseGameTime(m.end_time),
    cancelUntilMs: parseGameTime(m.cctcd),
    units: m.units && Object.keys(m.units).length ? m.units : undefined,
    other: dir === "out" ? { name: m.target_name, coords: m.target_coords } : { name: m.origin_name, coords: m.origin_coords },
  });
  const incoming = (v.queue_rally?.ra_i ?? []).map((m) => mk(m, "in")).sort((a, b) => a.endMs - b.endMs);
  const outgoing = (v.queue_rally?.ra_o ?? []).map((m) => mk(m, "out")).sort((a, b) => a.endMs - b.endMs);
  return { incoming, outgoing };
}

export const moveProgress = (e: MoveEntry, now: number) =>
  e.endMs > e.startMs ? Math.min(1, Math.max(0, (now - e.startMs) / (e.endMs - e.startMs))) : 1;

/** Which unit sets the pace of an outgoing army (its slowest), as a label: "Scholar pace". Incoming armies hide their
 * units, so the pace is estimated from the distance and the travel time. */
export function paceLabel(e: MoveEntry, from: [number, number] | null): string {
  if (e.units) {
    const s = slowestSpeed(e.units);
    if (s) return `${TROOP_NAMES[s.key]} pace`;
  }
  const to = parseCoords(e.other.coords);
  if (from && to && e.endMs > e.startMs) {
    const minPerField = (e.endMs - e.startMs) / 60_000 / Math.max(0.01, fieldsBetween(from, to));
    let best: TroopKey | null = null;
    let bestDiff = Infinity;
    for (const k of TROOP_KEYS) {
      const diff = Math.abs(UNITS[k].speed - minPerField);
      if (diff < bestDiff) {
        bestDiff = diff;
        best = k;
      }
    }
    if (best && bestDiff < 1.5) return `${TROOP_NAMES[best]} pace (est.)`;
  }
  return "";
}

/** Client-side travel preview for a selection: ms, pace unit. rally/prepare confirms it before sending. */
export function travelPreview(from: [number, number], to: [number, number], units: Partial<Record<TroopKey, number>>): { ms: number; pace: TroopKey } | null {
  const s = slowestSpeed(units);
  if (!s) return null;
  return { ms: travelMs(fieldsBetween(from, to), s.speed), pace: s.key };
}

// ------------------------------------------------------------------ market

export interface MerchantMove {
  m: MarketMovement;
  kind: "send" | "trade" | "return";
  dir: "in" | "out";
  startMs: number;
  endMs: number;
  other: { name: string; coords: string };
}

/** Merchant movements: outgoing first then incoming in the game's list; direction from the coords of this city. */
export function merchantMovements(v: Village): MerchantMove[] {
  const mine = v.village?.coords;
  return (v.queue_market ?? []).map((m) => {
    const dir: "in" | "out" = m.origin_coords === mine ? "out" : "in";
    const kind: MerchantMove["kind"] = m.primary_type === "s" ? "send" : m.primary_type === "t" ? "trade" : "return";
    return {
      m,
      kind,
      dir,
      startMs: parseGameTime(m.start_time),
      endMs: parseGameTime(m.end_time),
      other: dir === "out" ? { name: m.target_name, coords: m.target_coords } : { name: m.origin_name, coords: m.origin_coords },
    };
  });
}

export const merchantsNeeded = (amounts: Partial<Record<ResourceKey, number>>, carry = WORLD.market.carry) =>
  RESOURCE_KEYS.reduce((n, r) => n + Math.ceil(Math.max(0, amounts[r] ?? 0) / carry), 0);

export const merchantTravelMs = (from: [number, number], to: [number, number], worldSpeed = WORLD.speed) =>
  travelMs(fieldsBetween(from, to), WORLD.market.merchantSpeedMinPerField, worldSpeed, 1);

// ------------------------------------------------------------------ store & effects

export const storeItem = (v: Village, key: string): StoreItem | undefined => v.store?.items?.[key];

export const storefrontItems = (v: Village, front: string): StoreItem[] => {
  const f = v.store?.storefronts?.[front];
  const keys = Array.isArray(f?.items) ? (f!.items as string[]) : [];
  return keys.map((k) => v.store?.items?.[k]).filter((x): x is StoreItem => !!x);
};

export const effectExpiry = (v: Village, effectKey: string): number | null => {
  const e = v.active_effects?.[effectKey];
  return e ? e.expires_at : null;
};

export const owned = (v: Village, itemKey: string): number => v.store?.items?.[itemKey]?.amount_owned ?? 0;

/** The construction queue's slot count: 2, or 5 while the queue extender runs. */
export const queueSlots = (v: Village) => WORLD.queue.construction + (effectExpiry(v, "extend_construction_queue") ? WORLD.queue.extender : 0);

// ------------------------------------------------------------------ the city header line

export const cityCoords = (v: Village | undefined): [number, number] | null => parseCoords(v?.village?.coords);

export const continentLabel = (v: Village | undefined) => (v?.village ? `R${v.village.continent}` : "");

// ------------------------------------------------------------------ quests (QuestService::getState)

/** The quest list, whether the server sent a map (normal) or an array. */
export const questList = (v?: Village): QuestItem[] => {
  if (!v?.quests) return [];
  const q = v.quests as Record<string, QuestItem> | QuestItem[];
  const arr = Array.isArray(q) ? q : Object.values(q);
  return arr.filter((x) => x && typeof x === "object" && typeof x.id === "string");
};

export const claimableQuests = (v?: Village): number => questList(v).filter((q) => Number(q.done) === 1).length;

export const questCategories = (v?: Village): QuestCategory[] => {
  const c = v?.quest_categories;
  return Array.isArray(c) ? (c as QuestCategory[]).filter((x) => x && typeof x.name === "string" && Array.isArray(x.items)) : [];
};

export const auroraQuests = (v?: Village): AuroraQuests | null => {
  const a = v?.aurora_quests;
  return a && typeof a === "object" && !Array.isArray(a) ? (a as AuroraQuests) : null;
};

/** "200 wood · 20 Lancers · 1 Amulet" — a quest's award in words. */
export function awardWords(award: Record<string, number> | undefined, itemNames: Record<string, string> = {}): string {
  if (!award) return "";
  const parts: string[] = [];
  for (const [k, n] of Object.entries(award)) {
    const amt = Number(n) || 0;
    if (k === "r_w" || k === "r_c" || k === "r_i") parts.push(`${amt.toLocaleString("en-US")} ${k === "r_w" ? "wood" : k === "r_c" ? "clay" : "iron"}`);
    else if ((TROOP_KEYS as readonly string[]).includes(k)) parts.push(`${amt.toLocaleString("en-US")} ${amt === 1 ? TROOP_NAMES[k as TroopKey] : TROOP_NAMES[k as TroopKey] + "s"}`);
    else parts.push(`${amt.toLocaleString("en-US")} ${itemNames[k] ?? k.replace(/_/g, " ")}`);
  }
  return parts.join(" · ");
}
